/**
* Firebase Configuration and Initialization (initializeApp) for Chat Widget
* 
* Handles:
* - Multi Firebase projects configurations (staging, djc, iqpilot)
* - Dynamic initialization and management of multiple Firebase projects
* - Project key mapping ("1", "2", "3" > project IDs)
* 
* The project key is stored in the API key document (within the API key itself) in Firestore.
* When a widget initializes with an API key, it reads the firebaseProjectKey
* from that document and uses it to connect to the appropriate Firebase project.
*/

import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { getStorage, type FirebaseStorage } from 'firebase/storage'

// Types - Firebase project IDs and keys
export type FirebaseProjectId = 'staging' | 'djc' | 'iqpilot'
export type FirebaseProjectKey = '1' | '2' | '3'

// Interface - Firebase configuration
export interface FirebaseConfig {
    apiKey: string
    authDomain: string
    projectId: string
    storageBucket: string
    messagingSenderId: string
    appId: string
    measurementId?: string
}

// Registry: project key > project ID mapping
const PROJECT_KEY_REGISTRY: Record<FirebaseProjectKey, FirebaseProjectId> = {
    '1': 'staging',    // djc-leads-v2-staging
    '2': 'djc',        // facebook-api-59e5c
    '3': 'iqpilot',    // iqi-pilot
}

// Firebase configurations for each project (loaded from environment variables)
const firebaseConfigs: Record<FirebaseProjectId, FirebaseConfig> = {
    staging: {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY_STAGING || '',
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_STAGING || '',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_STAGING || 'djc-leads-v2-staging',
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_STAGING || '',
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_STAGING || '',
        appId: import.meta.env.VITE_FIREBASE_APP_ID_STAGING || '',
    },
    djc: {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY_DJC || '',
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_DJC || '',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_DJC || 'facebook-api-59e5c',
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_DJC || '',
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_DJC || '',
        appId: import.meta.env.VITE_FIREBASE_APP_ID_DJC || '',
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID_DJC,
    },
    iqpilot: {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY_IQPILOT || '',
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_IQPILOT || '',
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_IQPILOT || 'iqi-pilot',
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_IQPILOT || '',
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_IQPILOT || '',
        appId: import.meta.env.VITE_FIREBASE_APP_ID_IQPILOT || '',
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID_IQPILOT,
    },
}

// Functions base URLs for each project
const functionsUrls: Record<FirebaseProjectId, string> = {
    staging: 'https://asia-southeast1-djc-leads-v2-staging.cloudfunctions.net',
    djc: 'https://asia-southeast1-facebook-api-59e5c.cloudfunctions.net',
    iqpilot: 'https://asia-southeast1-iqi-pilot.cloudfunctions.net',
}

// Helper function - Get Firebase config by project key
function getFirebaseConfigByKey(projectKey: FirebaseProjectKey): FirebaseConfig | null {
    const projectId = PROJECT_KEY_REGISTRY[projectKey]
    if (!projectId) {
        console.error(`[Firebase] Unknown Firebase project key: ${projectKey}`)
        return null
    }
    return firebaseConfigs[projectId]
}

// Helper function - Get functions base URL by project key
export function getFunctionsBaseUrlByKey(projectKey: FirebaseProjectKey): string {
    const projectId = PROJECT_KEY_REGISTRY[projectKey]
    return functionsUrls[projectId] || ''
}

// Helper function - Get upload function URL by project key
export function getUploadFunctionUrl(projectKey: FirebaseProjectKey): string {
    const baseUrl = getFunctionsBaseUrlByKey(projectKey)
    return baseUrl ? `${baseUrl}/getCustomPlatformUploadUrl` : ''
}

// ============================================================================
// FIREBASE INITIALIZATION
// ============================================================================

// Cache of initialized Firebase apps by project key
const firebaseApps = new Map<string, FirebaseApp>()
const firestoreInstances = new Map<string, Firestore>()
const storageInstances = new Map<string, FirebaseStorage>()

// Initialize or get a Firebase app for a specific project key (InitializeApp)
export function getFirebaseAppForProjectKey(projectKey: FirebaseProjectKey): FirebaseApp | null {
    // Check cache first
    if (firebaseApps.has(projectKey)) {
        return firebaseApps.get(projectKey)!
    }

    // Get config from registry
    const config = getFirebaseConfigByKey(projectKey)
    if (!config) {
        console.error(`[Firebase] Failed to get config for project key: ${projectKey}`)
        return null
    }

    // Check if app exists in Firebase SDK's app registry
    const existingApps = getApps()
    const existingApp = existingApps.find(app => app.options.projectId === config.projectId)

    if (existingApp) {
        firebaseApps.set(projectKey, existingApp)
        return existingApp
    }

    // Initialize new app with unique name
    const appName = `firebase_${config.projectId}_${Date.now()}`
    const app = initializeApp(config, appName)
    firebaseApps.set(projectKey, app)

    console.log(`✅ Firebase initialized for project key: ${projectKey} (${config.projectId})`)
    return app
}

// Get Firestore instance for a specific project key (Firestore)
export function getFirestoreForProjectKey(projectKey: FirebaseProjectKey): Firestore | null {
    // Check cache first
    if (firestoreInstances.has(projectKey)) {
        return firestoreInstances.get(projectKey)!
    }

    // Get Firebase app for this project key
    const app = getFirebaseAppForProjectKey(projectKey)
    if (!app) {
        return null
    }

    // Get Firestore instance
    const db = getFirestore(app)
    firestoreInstances.set(projectKey, db)

    return db
}

// Get Storage instance for a specific project key (Storage)
export function getStorageForProjectKey(projectKey: FirebaseProjectKey): FirebaseStorage | null {
    // Check cache first
    if (storageInstances.has(projectKey)) {
        return storageInstances.get(projectKey)!
    }

    // Get Firebase app for this project key
    const app = getFirebaseAppForProjectKey(projectKey)
    if (!app) {
        return null
    }

    // Get Storage instance
    const storage = getStorage(app)
    storageInstances.set(projectKey, storage)

    return storage
}

// Clean up Firebase apps (useful for testing or when switching projects)
export function cleanupFirebaseApps() {
    firebaseApps.clear()
    firestoreInstances.clear()
    storageInstances.clear()
}
